import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import { formatInTimezone, useTimezone } from '@kioskgaming/ui';
import AppLayout from '../components/layout/AppLayout';
import { fetchMessagingCampaigns, type MessagingCampaignSummary } from '../services/api';

function formatUsd(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `$${n.toFixed(2)}`;
}

function formatDate(iso: string | null | undefined, timezone: string): string {
  return formatInTimezone(iso, timezone);
}

export default function MessagesHistoryPage() {
  const { timezone } = useTimezone();
  const [campaigns, setCampaigns] = useState<MessagingCampaignSummary[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchMessagingCampaigns();
      if (res.success && res.data?.campaigns) setCampaigns(res.data.campaigns);
    } catch {
      toast.error('Could not load campaigns');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <AppLayout>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-stone-900">Message history</h1>
          <p className="text-sm text-stone-600 mt-1">Past email and SMS campaigns with delivery status.</p>
        </div>
        <Link
          to="/messages/compose"
          className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
        >
          New message
        </Link>
      </div>

      {loading ? (
        <PageLoadingIndicator />
      ) : campaigns.length === 0 ? (
        <p className="text-sm text-stone-500">No campaigns yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-stone-200">
          <table className="min-w-full text-sm">
            <thead className="bg-stone-50 text-left text-stone-600">
              <tr>
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium">Channel</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Recipients</th>
                <th className="px-3 py-2 font-medium">Quoted</th>
                <th className="px-3 py-2 font-medium">Final</th>
                <th className="px-3 py-2 font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {campaigns.map((c) => (
                <tr key={c.id} className="hover:bg-stone-50">
                  <td className="px-3 py-2 whitespace-nowrap">{formatDate(c.createdAt, timezone)}</td>
                  <td className="px-3 py-2 uppercase">{c.channel}</td>
                  <td className="px-3 py-2 capitalize">{c.status}</td>
                  <td className="px-3 py-2">
                    {c.sentCount}/{c.eligibleCount} sent
                    {c.failedCount > 0 ? ` · ${c.failedCount} failed` : ''}
                  </td>
                  <td className="px-3 py-2">{formatUsd(c.quotedCostUsd)}</td>
                  <td className="px-3 py-2">{formatUsd(c.finalCostUsd)}</td>
                  <td className="px-3 py-2 text-right">
                    <Link to={`/messages/${c.id}`} className="text-emerald-700 font-medium hover:underline">
                      Details
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AppLayout>
  );
}
